import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Box } from "@salestrak/ui";
import { PageHeader, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter } from "#app/components/page-header";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { DangerZoneGroup } from "#app/components/settings/danger-zone";
import { ProfileFieldsGroup } from "#app/components/settings/profile-form";
import { LeaveWorkspaceDialog } from "#app/components/workspace-membership/leave-workspace-dialog";
import { useWorkspace } from "#app/lib/data/workspace-context";

function ProfilePage() {
  const { user, isLoading, error, refetch, updateUserField } =
    useUserSettings();
  const { activeWorkspace } = useWorkspace();
  const [leaveOpen, setLeaveOpen] = useState(false);

  if (isLoading && !user) {
    return (
      <Box fill="flex" overflow="auto">
        <PageHeader centered>
          <PageHeaderTitle>Profile</PageHeaderTitle>
        </PageHeader>
        {/* Mirrors the settled run: the 6-row profile fields card and the
          * 1-row danger zone. */}
        <SettingsPageSkeleton groups={[{ rows: 6 }, { rows: 1 }]} />
      </Box>
    );
  }

  if (error && !user) {
    return <LoadErrorState noun="profile" error={error} onRetry={() => { void refetch(); }} />;
  }

  return (
    <Box fill="flex" overflow="auto">
      <PageHeader centered>
        <PageHeaderTitle>Profile</PageHeaderTitle>
      </PageHeader>
      <Box direction="column" align="center" paddingY="md" paddingX="md">
        <Box direction="column" gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
          {user ? (
            <ProfileFieldsGroup user={user} onFieldSave={updateUserField} />
          ) : null}

          {/* The row is always offered; whether the viewer MAY leave (the
            * sole Owner and the last member may not) is the dialog's answer,
            * with the doors that lead out of that state. */}
          <DangerZoneGroup
            dangerZone={[
              {
                id: "leave-workspace",
                label: "Remove yourself from workspace",
                subtitle: "Your work stays, with your name on it.",
                buttonLabel: "Leave workspace...",
                variant: "destructive",
                onClick: activeWorkspace ? () => setLeaveOpen(true) : undefined,
              },
            ]}
          />
          {leaveOpen && activeWorkspace ? (
            <LeaveWorkspaceDialog
              workspace={activeWorkspace}
              onOpenChange={(open) => {
                if (!open) setLeaveOpen(false);
              }}
            />
          ) : null}

          <SettingsPageGutter />
        </Box>
      </Box>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/profile")({
  component: ProfilePage,
});
